import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Inject,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { Router } from '@angular/router';
import { PopupRef, PopupService } from '@progress/kendo-angular-popup';
import { SVGIcon, plusIcon } from '@progress/kendo-svg-icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatatableComponent } from '../../../core/components/datatable/datatable.component';
import { OrganizationComponent } from './components/organization/organization.component';
import { BaseComponent } from '../../../core/shared/common/base.component';
import { AccountTypeResult, Organization } from '../../../core/models/entities';
import { GeneralOrganizationService } from './components/general/service/general-organization.service';
import { Events, EventService } from 'src/app/core/services/event.service';
import { BillingService, OrganizationStatsMap } from './services/billing.service';
import { environment } from '../../../../environments/environment';
import DataTable from 'datatables.net-dt';

@Component({
  selector: 'app-organizations',
  standalone: true,
  imports: [CommonModule, ButtonsModule, DropDownListModule, DatatableComponent],
  templateUrl: './organizations.page.html',
  styleUrl: './organizations.page.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationsPage extends BaseComponent {
  @ViewChild(DatatableComponent) private table?: DatatableComponent;

  public router = inject(Router);
  private popupService = inject(PopupService);
  private eventService = inject(EventService);
  public generalOrgService = inject(GeneralOrganizationService);
  private billingService = inject(BillingService);

  public plusIcon: SVGIcon = plusIcon;
  public accountTypeItems = signal<AccountTypeResult[]>([]);
  public billingStats = signal<OrganizationStatsMap>({});

  public datatableUrl = `${environment.apiUrl}/v1/organization/administrative/organization/`;

  private popupRef: PopupRef | undefined;

  public statuses = [
    { text: 'Active', value: 1 },
    { text: 'Disabled', value: 0 },
  ];

  private statusFilter: number | null = null;
  private accountTypeFilter: number | null = null;

  bodyElement: ElementRef;

  public datatableColumns = [
    { data: 'id', name: 'id', title: 'ID', type: 'number' },
    { data: 'name', name: 'name', title: 'Name', type: 'string', render: DataTable.render.text() },
    {
      data: null,
      name: 'parent__name',
      title: 'Parent',
      type: 'string',
      // parent is nullable, so the row is read defensively instead of pointing
      // the column at the nested path.
      render: (_data: unknown, _type: unknown, row: Organization) => DataTable.util.escapeHtml(row.parent?.name ?? ''),
    },
    {
      data: 'status',
      name: 'status',
      title: 'Status',
      type: 'string',
      render: (data: number) =>
        data ? '<span class="badge bg-success">Active</span>' : '<span class="badge bg-danger">Inactive</span>',
    },
    {
      data: 'account_type_display',
      name: 'account_type',
      title: 'Type',
      type: 'string',
      render: DataTable.render.text(),
    },
    {
      data: null,
      name: 'subscription_count',
      title: '# Subscriptions',
      type: 'number',
      // Billing counts are not part of the organization payload; they come from
      // a separate call, so the column reads the map and the table is redrawn
      // once it resolves.
      orderable: false,
      render: (_data: unknown, _type: unknown, row: Organization) =>
        this.billingStats()[row.id!]?.subscription_count ?? '–',
    },
    {
      data: null,
      name: 'pricing_exception_count',
      title: '# Pricing Exceptions',
      type: 'number',
      orderable: false,
      render: (_data: unknown, _type: unknown, row: Organization) =>
        this.billingStats()[row.id!]?.pricing_exception_count ?? '–',
    },
  ];

  constructor(@Inject(DOCUMENT) private document: Document) {
    super();
    this.bodyElement = new ElementRef(this.document.body);
  }

  ngOnInit() {
    this.loadAccountTypes();
    this.loadBillingStats();
  }

  private loadBillingStats(): void {
    this.billingService
      .getOrganizationStats()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (stats) => {
          this.billingStats.set(stats);
          // The first draw may already have happened with an empty map.
          this.table?.reload();
        },
        error: (err) => console.error('Failed to load billing stats:', err),
      });
  }

  private loadAccountTypes() {
    this.generalOrgService
      .getAccountTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (accountTypes) => {
          this.accountTypeItems.set(accountTypes.results as AccountTypeResult[]);
        },
        error: (error) => {
          console.error('Error fetching account types:', error);
        },
      });
  }

  public statusChange($event: { text: string; value: number | null }): void {
    this.statusFilter = $event?.value ?? null;
    this.applyFilters();
  }

  public accountTypeChange($event: { display_text: string; id: number | null }): void {
    this.accountTypeFilter = $event?.id ?? null;
    this.applyFilters();
  }

  private applyFilters(): void {
    const clauses: string[] = [];
    if (this.statusFilter !== null) {
      clauses.push(`status eq ${this.statusFilter}`);
    }
    if (this.accountTypeFilter !== null) {
      clauses.push(`account_type eq ${this.accountTypeFilter}`);
    }
    this.table?.setFilters(clauses.length ? { $filter: clauses.join(' and ') } : {});
  }

  public addOrg(anchor: ElementRef | HTMLElement): void {
    this.togglePopup(anchor);
  }

  public togglePopup(anchor: ElementRef | HTMLElement): void {
    if (this.popupRef) {
      this.popupRef.close();
      this.popupRef = undefined;
    } else {
      this.popupRef = this.popupService.open({
        anchor: anchor,
        content: OrganizationComponent,
        animate: { type: 'slide', direction: 'left', duration: 200 },
        anchorAlign: { horizontal: 'right', vertical: 'top' },
        popupAlign: { horizontal: 'right', vertical: 'top' },
      });

      const userComponent = this.popupRef.content.instance as OrganizationComponent;
      userComponent.close.subscribe(() => {
        this.table?.reload();
        this.togglePopup(anchor);
      });
    }
  }

  public editOrg(row: unknown) {
    const organization = row as Organization;
    this.router.navigate(['/admin/organizations/general'], {
      queryParams: {
        organizationId: organization.id,
        account_type: organization.account_type,
      },
    });
    this.eventService.emit(Events.OrgSelectionChange.type, new Events.WhiteLabelChange(organization));
  }
}
